import { createCompoundIndexValue, createLowerBoundIndexValue, createUpperBoundIndexValue, getLegacyDotOidSubIdRange, getOidRoot, getOidSubIdRange, } from '@verdant-web/common';
import { IdbService } from '../IdbService.js';
import { closeDatabase, getSizeOfObjectStore } from '../util.js';
export class IdbMetadataDb extends IdbService {
    constructor(db, ctx) {
        super(db, ctx);
        this.ctx = ctx;
        this.transaction = async (opts, procedure) => {
            const tx = this.createTransaction(opts.storeNames, {
                mode: opts.mode,
                abort: opts.abort,
            });
            const result = await procedure(tx);
            return result;
        };
        this.getAckInfo = async () => {
            const result = await this.run('info', (store) => store.get('ack'));
            if (result) {
                return result;
            }
            else {
                return {
                    globalAckTimestamp: null,
                };
            }
        };
        this.setGlobalAck = async (ack) => {
            await this.run('info', (store) => store.put({ type: 'ack', globalAckTimestamp: ack }), { mode: 'readwrite' });
        };
        this.getLocalReplica = async (opts) => {
            return this.run('info', (store) => store.get('localReplicaInfo'), opts);
        };
        this.updateLocalReplica = async (data, opts) => {
            try {
                await this.run('info', (store) => store.put(Object.assign(Object.assign({}, data), { type: 'localReplicaInfo' })), {
                    mode: 'readwrite',
                    transaction: opts === null || opts === void 0 ? void 0 : opts.transaction,
                });
            }
            catch (e) {
                this.ctx.log('critical', 'Error updating local replica', data, e);
                throw e;
            }
        };
        this.iterateDocumentBaselines = async (rootOid, iterator, opts) => {
            await this.iterate('baselines', (store) => {
                const root = getOidRoot(rootOid);
                const [start, end] = getOidSubIdRange(rootOid);
                // FIXME: get rid of legacy dot OIDs...
                const [dotStart, dotEnd] = getLegacyDotOidSubIdRange(rootOid);
                return [
                    store.openCursor(IDBKeyRange.only(root)),
                    store.openCursor(IDBKeyRange.bound(start, end, false, false)),
                    store.openCursor(IDBKeyRange.bound(dotStart, dotEnd, false, false)),
                ];
            }, iterator, opts);
        };
        this.iterateCollectionBaselines = async (collection, iterator, opts) => {
            await this.iterate('baselines', (store) => {
                return [
                    store.openCursor(IDBKeyRange.bound(collection, collection + '\uffff', false, false)),
                ];
            }, iterator, opts);
        };
        this.iterateAllBaselines = async (iterator, opts) => {
            await this.iterate('baselines', (store) => store.index('timestamp').openCursor(), iterator, opts);
        };
        this.getBaseline = (oid, opts) => {
            return this.run('baselines', (store) => store.get(oid), opts);
        };
        this.setBaselines = async (baselines, opts = writeOpts) => {
            await this.runAll('baselines', (store) => baselines.map((b) => store.put(b)), opts);
        };
        this.deleteBaseline = async (oid, opts = writeOpts) => {
            await this.run('baselines', (store) => store.delete(oid), opts);
        };
        this.iterateDocumentOperations = (rootOid, iterator, opts) => {
            return this.iterate('operations', (store) => {
                const index = store.index('d_t');
                const start = createLowerBoundIndexValue(rootOid);
                const end = (opts === null || opts === void 0 ? void 0 : opts.to)
                    ? createCompoundIndexValue(rootOid, opts.to)
                    : createUpperBoundIndexValue(rootOid);
                const range = IDBKeyRange.bound(start, end, false, false);
                return index.openCursor(range);
            }, iterator, opts);
        };
        this.iterateEntityOperations = (oid, iterator, opts) => {
            // NOTE: this is simplified from original impl.
            // perhaps I missed some nuance as to why it was
            // so complex before?
            return this.iterate('operations', (store) => {
                const start = createLowerBoundIndexValue(oid);
                const end = (opts === null || opts === void 0 ? void 0 : opts.to)
                    ? createCompoundIndexValue(oid, opts.to)
                    : createUpperBoundIndexValue(oid);
                const range = IDBKeyRange.bound(start, end, false, false);
                return store.openCursor(range);
            }, iterator, opts);
        };
        this.deleteEntityOperations = (oid, opts) => {
            return this.iterate('operations', (store) => {
                const start = createLowerBoundIndexValue(oid);
                const end = (opts === null || opts === void 0 ? void 0 : opts.to)
                    ? createCompoundIndexValue(oid, opts.to)
                    : createUpperBoundIndexValue(oid);
                const range = IDBKeyRange.bound(start, end, false, false);
                return store.openCursor(range);
            }, (op, store) => {
                store.delete(op.oid_timestamp);
            }, opts);
        };
        this.iterateCollectionOperations = (collection, iterator, opts) => {
            return this.iterate('operations', (store) => {
                return store.openCursor(IDBKeyRange.bound(collection, collection + '\uffff', false, false), 'next');
            }, iterator, opts);
        };
        this.iterateLocalOperations = (iterator, opts) => {
            return this.iterate('operations', (store) => {
                const start = (opts === null || opts === void 0 ? void 0 : opts.after)
                    ? createCompoundIndexValue(true, opts.after)
                    : createLowerBoundIndexValue(true);
                const end = createUpperBoundIndexValue(true);
                const index = store.index('l_t');
                return index.openCursor(
                // NOTE: differs from original impl -- last arg is 'false' instead of 'true'
                IDBKeyRange.bound(start, end, !!(opts === null || opts === void 0 ? void 0 : opts.after), false), 'next');
            }, iterator);
        };
        this.iterateAllOperations = (iterator, opts) => {
            return this.iterate('operations', (store) => {
                const start = (opts === null || opts === void 0 ? void 0 : opts.from)
                    ? createLowerBoundIndexValue(opts.from)
                    : undefined;
                const end = (opts === null || opts === void 0 ? void 0 : opts.before)
                    ? createUpperBoundIndexValue(opts.before)
                    : createLowerBoundIndexValue(true);
                const range = start && end
                    ? IDBKeyRange.bound(start, end, false, true)
                    : start
                        ? IDBKeyRange.lowerBound(start, false)
                        : end
                            ? IDBKeyRange.upperBound(end, true)
                            : undefined;
                return store.index('timestamp').openCursor(range, 'next');
            }, iterator, opts);
        };
        this.addOperations = async (ops, opts = writeOpts) => {
            let affected = new Set();
            await this.runAll('operations', (store) => ops.map((op) => {
                affected.add(getOidRoot(op.oid));
                return store.put(this.addOperationIndexes(op));
            }), opts);
            return Array.from(affected);
        };
        this.reset = async ({ clearReplica, transaction, } = {}) => {
            const tx = transaction ||
                this.createTransaction(['info', 'operations', 'baselines'], {
                    mode: 'readwrite',
                });
            await Promise.all([
                this.resetLocalReplica(tx, clearReplica),
                this.resetBaselines(tx),
                this.resetOperations(tx),
            ]);
        };
        this.stats = async () => {
            const ops = await getSizeOfObjectStore(this.db, 'operations');
            const baselines = await getSizeOfObjectStore(this.db, 'baselines');
            return { operationsSize: ops, baselinesSize: baselines };
        };
        this.resetLocalReplica = async (tx, clear = false) => {
            if (clear) {
                return this.run('info', (store) => store.delete('localReplicaInfo'), {
                    mode: 'readwrite',
                    transaction: tx,
                });
            }
            else {
                const localInfo = await this.getLocalReplica({
                    transaction: tx,
                });
                if (localInfo) {
                    localInfo.ackedLogicalTime = null;
                    localInfo.lastSyncedLogicalTime = null;
                    await this.run('info', (store) => store.put(Object.assign(Object.assign({}, localInfo), { type: 'localReplicaInfo' })), {
                        mode: 'readwrite',
                        transaction: tx,
                    });
                }
            }
        };
        this.resetBaselines = async (tx) => {
            return this.clear('baselines', tx);
        };
        this.resetOperations = async (tx) => {
            return this.clear('operations', tx);
        };
        this.addOperationIndexes = (op) => {
            return Object.assign(Object.assign({}, op), { oid_timestamp: createCompoundIndexValue(op.oid, op.timestamp), l_t: createCompoundIndexValue(op.isLocal, op.timestamp), d_t: createCompoundIndexValue(getOidRoot(op.oid), op.timestamp) });
        };
        this.addDispose(() => {
            this.ctx.log('info', `Closing metadata DB for`, this.ctx.namespace);
            return closeDatabase(db);
        });
    }
}
const writeOpts = { mode: 'readwrite' };
//# sourceMappingURL=IdbMetadataDb.js.map